/**
 * Region-aware sowing calendar — pure client-side, no API/LLM calls.
 *
 * The MONTHLY_CALENDAR tips are calibrated for a temperate Nordic climate
 * (~55°N). We shift which month's advice is shown based on the user's
 * latitude so warmer regions see the season start earlier, colder regions
 * later, and the Southern Hemisphere is flipped by 6 months.
 */

// Latitude band → climate zone + how many months the local season runs
// AHEAD of the Nordic baseline (negative = behind).
function bandFromLatitude(absLat) {
    if (absLat >= 60) return { zone: 'cold', baseShift: -1 };
    if (absLat >= 48) return { zone: 'temperate', baseShift: 0 };
    if (absLat >= 40) return { zone: 'mild', baseShift: 1 };
    return { zone: 'warm', baseShift: 2 };
}

/**
 * @param {number|null|undefined} lat
 * @returns {{zone:string, shift:number, south:boolean}|null} null when no location.
 *   shift is normalised to 0..11 and applied as:
 *   baselineMonth = ((displayMonth - 1 + shift) % 12) + 1
 */
export function getClimate(lat) {
    if (lat === null || lat === undefined || Number.isNaN(Number(lat))) return null;
    const south = lat < 0;
    const { zone, baseShift } = bandFromLatitude(Math.abs(lat));
    const shift = (((baseShift + (south ? 6 : 0)) % 12) + 12) % 12;
    return { zone, shift, south };
}

// Display month (1-12) → which baseline month's advice to show.
export function baselineMonthFor(displayMonth, shift) {
    return (((displayMonth - 1 + shift) % 12) + 12) % 12 + 1;
}

export const ZONE_EMOJI = { warm: '🔆', mild: '🌤️', temperate: '🍃', cold: '❄️' };

/**
 * Region-aware sowing status for a plant's baseline [start,end] window.
 * Converts the current real month into the equivalent baseline month for the
 * user's climate (same shift as the calendar), then checks the window.
 * @returns {'now'|'soon'|null}
 */
export function sowStatus(window, climate, now = new Date()) {
    if (!window) return null;
    const shift = climate ? climate.shift : 0;
    const [start, end] = window;
    const eff = baselineMonthFor(now.getMonth() + 1, shift); // current month in baseline terms
    const inWindow = start <= end
        ? eff >= start && eff <= end
        : eff >= start || eff <= end; // wrap-around safety
    if (inWindow) return 'now';
    const soonMonth = ((start - 2 + 12) % 12) + 1; // the month just before the window opens
    if (eff === soonMonth) return 'soon';
    return null;
}
